import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { createColumnsForObjectData, SalvTable, SalvTablePaginationType, Size } from '@salvapp/ui-components';
import { InternalApiFinalRisk } from '../api-internal';
import { formatEnumLabel } from '../common/helper';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import { riskLevelColourStyle } from './riskLevelColours';

const PAGE_SIZE = 10;

interface Props {
  events: InternalApiFinalRisk[];
  isLoading?: boolean;
  isError?: boolean;
}

export const RiskHistoryTable = ({ events, isLoading, isError }: Props) => {
  const [page, setPage] = useState(0);
  const total = events.length;

  useEffect(() => setPage(0), [events]);

  const columns = useMemo(
    () =>
      createColumnsForObjectData<InternalApiFinalRisk>([
        {
          accessorKey: 'createdTime',
          header: 'Date',
          cell: (row) => (
            <>
              <div>{dayjs(row.createdTime).format('D MMM')}</div>
              <div className='date-hint'>{dayjs(row.createdTime).format('HH:mm')}</div>
            </>
          ),
        },
        {
          accessorKey: 'level',
          header: 'Level',
          cell: (row) => (
            <span className='text-uppercase small' style={riskLevelColourStyle(row.level)}>
              {formatEnumLabel(row.level)}
            </span>
          ),
        },
        {
          accessorKey: 'score',
          header: 'Score',
          cell: (row) => row.score ?? '--',
        },
        {
          accessorKey: 'event',
          header: 'Event',
          cell: (row) => (row.event ? formatEnumLabel(row.event) : '--'),
        },
        {
          accessorKey: 'reason',
          header: 'Reason',
          cell: (row) => row.reason ?? '--',
        },
        {
          accessorKey: 'comment',
          header: 'Comment',
          cell: (row) => row.comment ?? '--',
        },
        {
          accessorKey: 'createdBy',
          header: 'Changed by',
          cell: (row) => row.createdBy ?? '--',
        },
      ]),
    []
  );

  return (
    <div>
      {isError ? (
        <DataLoadingErrorFallback message='Failed to load risk history' />
      ) : (
        <SalvTable<InternalApiFinalRisk>
          className='risk-table'
          size={Size.SMALL}
          isLoading={isLoading}
          columns={columns}
          data={events.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)}
          emptyMessage='No events in the selected range.'
          pagination={{
            type: SalvTablePaginationType.PAGINATION,
            total,
            state: { pageIndex: page, pageSize: PAGE_SIZE },
            setPagination: ({ pageIndex }) => setPage(pageIndex),
          }}
        />
      )}
    </div>
  );
};

export default RiskHistoryTable;
